import { zodResolver } from "@hookform/resolvers/zod";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { MAX_NARRATIVES_PER_SEARCH, zNarrative } from "#next/utils/shared";
import { RouterOutput, trpc } from "#next/utils/trpc";
import { ExplorerModalStyle } from "#next/components/modals/styles";
import { TooltipForDisabledComponent } from "#next/components/search-builder/Block";
import { RenderQuery } from "#next/components/base/RenderQuery";
import { Dispatch, SetStateAction } from "react";
import { invisibleScrollStyle } from "#next/components/post/SinglePost";

type TNarratives = RouterOutput["savedSearch"]["getNarratives"];

export const NarrativesModal = ({
  narrativesModalIsOpen,
  searchId,
  setNarrativesModalIsOpen,
}: {
  narrativesModalIsOpen: boolean;
  searchId: string;
  setNarrativesModalIsOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  return (
    <RenderQuery
      useQuery={trpc.savedSearch.getNarratives}
      input={{ savedSearchId: searchId }}
      render={(data) => {
        return (
          <NarrativesModalUI
            narrativesModalIsOpen={narrativesModalIsOpen}
            narratives={data}
            setNarrativesModalIsOpen={setNarrativesModalIsOpen}
            searchId={searchId}
          />
        );
      }}
    />
  );
};

const zForm = z.object({
  narratives: z.array(z.object({ narrative: zNarrative })),
});

const NarrativesModalUI = ({
  narrativesModalIsOpen,
  narratives,
  searchId,
  setNarrativesModalIsOpen,
}: {
  narrativesModalIsOpen: boolean;
  narratives: TNarratives;
  searchId: string;
  setNarrativesModalIsOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid, errors },
  } = useForm({
    defaultValues: { narratives },
    resolver: zodResolver(zForm),
    mode: "onBlur",
  });

  zForm.parse({ narratives });

  const { fields, append, remove } = useFieldArray({
    name: "narratives",
    control,
  });

  const utils = trpc.useUtils();

  const save = trpc.savedSearch.setNarratives.useMutation({
    onSuccess: (data) => {
      utils.savedSearch.getNarratives.setData(
        { savedSearchId: searchId },
        data,
      );
      setNarrativesModalIsOpen(false);
    },
  });
  const onSave = () => {
    handleSubmit((data) => {
      save.mutate({
        narratives: Array.from(
          new Set(data.narratives.map((item) => item.narrative)),
        ),
        savedSearchId: searchId,
      });
    })();
  };

  return (
    <Modal
      open={narrativesModalIsOpen}
      onClose={() => setNarrativesModalIsOpen(false)}
    >
      <Box
        sx={{ ...ExplorerModalStyle, ...invisibleScrollStyle, height: "auto" }}
      >
        <Grid container direction="column">
          <Typography variant="h2" sx={{ mb: 1 }}>
            Manage Narratives
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Narratives enable natural-language filtering on top of project
            results. Use narratives to narrow project results to those that are
            related to a narrative, as an alternative to adding boolean logic.
          </Typography>
          <Typography>
            Projects can have a maximum of {MAX_NARRATIVES_PER_SEARCH}{" "}
            narratives. Each narrative must contain at least 3 words, and no
            more than 25 words.
          </Typography>

          <Divider sx={{ my: 2 }} />
          <Grid item container direction="column" alignItems="start">
            {fields.map((field, number) => {
              const error = errors.narratives
                ? errors.narratives[number]?.narrative?.message
                : undefined;
              return (
                <FormControl key={field.id} sx={{ mb: 2 }}>
                  <Controller
                    name={`narratives.${number}.narrative`}
                    control={control}
                    render={(data) => {
                      return (
                        <Grid container>
                          <Grid item>
                            <TextField
                              defaultValue={data.field.value}
                              onChange={data.field.onChange}
                              sx={{ width: "30rem" }}
                              onBlur={data.field.onBlur}
                              error={!!error}
                              helperText={error ?? ""}
                            />
                          </Grid>
                          <Grid item>
                            <IconButton onClick={() => remove(number)}>
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    }}
                  />
                </FormControl>
              );
            })}
            <TooltipForDisabledComponent
              disabledTooltipText={
                fields.length >= MAX_NARRATIVES_PER_SEARCH
                  ? "You have reached the maximum number of narratives"
                  : ""
              }
            >
              <Button
                disabled={fields.length >= MAX_NARRATIVES_PER_SEARCH}
                variant="outlined"
                onClick={() => append({ narrative: "" })}
              >
                Add Narrative
              </Button>
            </TooltipForDisabledComponent>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 6, mb: 2 }} />
        <Grid container>
          <Grid item flexGrow="1">
            <Button
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                setNarrativesModalIsOpen(false);
              }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="success"
              onClick={onSave}
              disabled={!isDirty || !isValid}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
